// pages/overlay/Stinger.jsx
// Two modes:
//   /overlay/stinger         — preview: plays the animation once in the browser
//   /overlay/stinger?render  — renders the animation to a canvas frame-by-frame,
//                              encodes to WebM, and auto-downloads the file.
//                              Dan opens this once, gets the .webm, and points
//                              OBS Stinger transition at the downloaded file.
//
// Animation: diagonal brand wipe (blue gradient) sweeps from left to right
// with a gold accent stripe. JoP logo flashes at the midpoint. ~1.2s total.
// Transition point for OBS: 600ms.
//
// Orientation: the stinger is a *file*, not a live source, so orientation is
// picked at render time — `?portrait` (or a portrait-shaped viewport) renders
// 1080×1920 for the vertical rig; default stays 1920×1080.

import { useEffect, useRef, useState } from "react";

const DIMS = { landscape: { W: 1920, H: 1080 }, portrait: { W: 1080, H: 1920 } };
const FPS = 60;
const COVER_MS  = 480;
const HOLD_MS   = 0;
const REVEAL_MS = 480;
const TOTAL_MS  = COVER_MS + HOLD_MS + REVEAL_MS;
const TOTAL_FRAMES = Math.ceil((TOTAL_MS / 1000) * FPS);

// Colors
const BLUE = "#1f59db";
const NAVY = "#1d52a0";
const PINK = "#ef537e";
const GOLD = "#fcb31f";
const CREAM = "#ffdaad";

function drawFrame(ctx, frameNum, logoImg, W, H) {
  const t = frameNum / FPS;
  const ms = t * 1000;

  ctx.clearRect(0, 0, W, H);

  // Phase 1: cover (panel slides in from left until it fully covers the screen)
  // Phase 2: hold (fully covered — logo flash)
  // Phase 3: reveal (panel continues sliding right until fully off-screen)
  //
  // The panel is exactly screen-width. It starts fully off-screen left,
  // slides right to cover the screen, holds, then continues right until
  // fully off-screen right. Simple linear travel with smoothstep easing.

  const SKEW_DEG = 12;
  const skewPx = H * Math.tan(SKEW_DEG * Math.PI / 180);

  // Progress 0→1 across the full animation. The panel's left edge travels
  // from -(W + skewPx) to +(W + skewPx), with the middle being "fully covered."
  let progress;
  if (ms < COVER_MS) {
    const p = ms / COVER_MS;
    progress = p * p * (3 - 2 * p); // smoothstep 0→1
  } else if (ms < COVER_MS + HOLD_MS) {
    progress = 1;
  } else if (ms < TOTAL_MS) {
    const p = (ms - COVER_MS - HOLD_MS) / REVEAL_MS;
    const eased = p * p * (3 - 2 * p);
    progress = 1 + eased; // 1→2
  } else {
    progress = 2;
  }

  // Panel shape: both edges skewed at the same angle. The panel is W wide
  // at center height, with each edge offset by skewPx/2 (half goes left at
  // bottom, half goes right at top) so both sides look identical.
  const panelW = W + skewPx; // just enough overshoot for full coverage
  const panelLeft = (progress - 1) * (W + skewPx) - skewPx / 2;

  // Only draw if the panel is at least partially on screen
  if (panelLeft < W + skewPx * 2 && panelLeft + panelW + skewPx > -skewPx) {
    ctx.save();
    ctx.beginPath();
    const x = panelLeft;
    // Both edges lean at the same angle (skewPx offset top-to-bottom)
    ctx.moveTo(x + skewPx, 0);           // top-left
    ctx.lineTo(x + panelW + skewPx, 0);  // top-right
    ctx.lineTo(x + panelW, H);           // bottom-right
    ctx.lineTo(x, H);                    // bottom-left
    ctx.closePath();

    // Gradient aligned with the 12° skew — runs perpendicular to the
    // skewed edges so colors band parallel to the wipe direction
    const grd = ctx.createLinearGradient(x + skewPx, 0, x, H);
    // Hero-style blue gradient — bright JoP blue to deep navy
    grd.addColorStop(0, "#4aa4e1");     // sky blue (bright edge)
    grd.addColorStop(0.25, "#1f59db");  // JoP primary blue
    grd.addColorStop(0.55, "#1a4b9e");  // mid blue
    grd.addColorStop(0.8, "#0e2e46");   // deep blue
    grd.addColorStop(1, "#081a2a");     // near-black navy
    ctx.fillStyle = grd;
    ctx.fill();

    // Gold accent stripe on the leading/trailing edge
    const stripeW = 6;
    if (progress <= 1) {
      ctx.beginPath();
      ctx.moveTo(x + panelW + skewPx, 0);
      ctx.lineTo(x + panelW + skewPx + stripeW, 0);
      ctx.lineTo(x + panelW + stripeW, H);
      ctx.lineTo(x + panelW, H);
      ctx.closePath();
    } else {
      ctx.beginPath();
      ctx.moveTo(x + skewPx - stripeW, 0);
      ctx.lineTo(x + skewPx, 0);
      ctx.lineTo(x, H);
      ctx.lineTo(x - stripeW, H);
      ctx.closePath();
    }
    ctx.fillStyle = GOLD;
    ctx.shadowColor = GOLD;
    ctx.shadowBlur = 15;
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.restore();
  }

  // Dr. Strange portal sparks — particles spawn at the wipe's edge and
  // linger in place as the wipe moves past, creating a trailing wake of
  // magic. Each spark has a fixed position (determined by its spawn time)
  // and fades out over its lifetime.
  {
    const skewAngle = Math.atan2(-skewPx, H);
    const edgeSkewPerY = -skewPx / H;

    // Spawn sparks at regular intervals during the wipe motion
    const SPARK_INTERVAL = 3; // spawn a set every N frames
    const SPARK_LIFETIME = 20; // frames a spark lives
    const totalSpawnFrames = TOTAL_FRAMES;

    for (let spawnFrame = 2; spawnFrame < totalSpawnFrames; spawnFrame += SPARK_INTERVAL) {
      const age = frameNum - spawnFrame;
      if (age < 0 || age > SPARK_LIFETIME) continue;

      // What was the edge position when this spark was born?
      const spawnMs = (spawnFrame / FPS) * 1000;
      let spawnProgress;
      if (spawnMs < COVER_MS) {
        const p = spawnMs / COVER_MS;
        spawnProgress = p * p * (3 - 2 * p);
      } else if (spawnMs < COVER_MS + HOLD_MS) {
        spawnProgress = 1;
      } else if (spawnMs < TOTAL_MS) {
        const p = (spawnMs - COVER_MS - HOLD_MS) / REVEAL_MS;
        spawnProgress = 1 + p * p * (3 - 2 * p);
      } else {
        continue;
      }

      const isEntering = spawnProgress <= 1;
      const spawnPanelLeft = (spawnProgress - 1) * (W + skewPx) - skewPx;
      const spawnEdgeX = isEntering
        ? spawnPanelLeft + panelW + skewPx
        : spawnPanelLeft + skewPx;

      // Fade: bright at birth, fades to nothing
      const lifeFrac = age / SPARK_LIFETIME;
      const fadeAlpha = lifeFrac < 0.1 ? lifeFrac / 0.1 : Math.max(0, 1 - (lifeFrac - 0.1) / 0.9);

      const spawnSeed = Math.floor(spawnFrame * 7) % 1000;

      // 7 sparks per batch: 2 big + 5 small, distributed across full height.
      // Particles are assigned vertical zones to prevent the top half from
      // being empty while still feeling organic within each zone.
      for (let i = 0; i < 7; i++) {
        const s1 = ((spawnSeed + i * 127) % 1000) / 1000;
        const s2 = ((spawnSeed + i * 311) % 1000) / 1000;
        const s3 = ((spawnSeed + i * 199) % 1000) / 1000;

        // Stagger every other particle's lifecycle by a per-particle time offset.
        // This breaks the "grid of lights" look — particles along the edge
        // don't all ignite and fade in lockstep.
        const timeOffset = (i % 2 === 0) ? 0 : (2 + s1 * 4); // 0 or 2-6 frame offset
        const effectiveAge = age - timeOffset;
        if (effectiveAge < 0 || effectiveAge > SPARK_LIFETIME) continue;
        const effectiveLifeFrac = effectiveAge / SPARK_LIFETIME;
        const effectiveFadeAlpha = effectiveLifeFrac < 0.1 ? effectiveLifeFrac / 0.1 : Math.max(0, 1 - (effectiveLifeFrac - 0.1) / 0.9);

        const isBig = i < 2;
        // Zone-jittered Y: divide height into zones, place within zone + jitter
        const zone = i / 7;
        const zoneJitter = (s1 - 0.5) * (H / 5);
        const py = zone * H + zoneJitter;
        const edgeAtY = spawnEdgeX + edgeSkewPerY * Math.max(0, Math.min(H, py));
        const spawnDrift = (s2 - 0.4) * (isBig ? 40 : 25);

        // Post-spawn drift — each spark floats away from the edge over its lifetime
        const driftDir = isEntering ? 1 : -1;
        const driftX = spawnDrift + effectiveAge * (1.5 + s3 * 2) * driftDir;
        // Upward drift bias — sparks rise off the edge at an angle
        const driftY = effectiveAge * (-1.8 + s2 * 1.2);

        const px = edgeAtY + driftX;
        const pyNow = py + driftY;

        if (px < -60 || px > W + 60) continue;

        const size = isBig ? (18 + s3 * 22) : (3 + s3 * 6);
        const sizeNow = size * (1 - effectiveLifeFrac * 0.5);
        const alpha = effectiveFadeAlpha * (isBig ? 0.9 : 0.65);

        if (alpha < 0.02 || sizeNow < 1) continue;

        const color = s3 > 0.4 ? GOLD : CREAM;

        // Comet streak — tapered line pointing back toward the edge
        const streakLen = isBig ? sizeNow * 2.5 : sizeNow * 1.5;
        const streakAngle = Math.atan2(driftY, driftX * driftDir);
        const tailX = px - Math.cos(streakAngle) * streakLen;
        const tailY = pyNow - Math.sin(streakAngle) * streakLen;

        ctx.save();
        ctx.globalAlpha = alpha * 0.5;
        const grad = ctx.createLinearGradient(tailX, tailY, px, pyNow);
        grad.addColorStop(0, "transparent");
        grad.addColorStop(1, color);
        ctx.strokeStyle = grad;
        ctx.lineWidth = sizeNow * 0.6;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(px, pyNow);
        ctx.stroke();
        ctx.restore();

        // Bright head dot
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = sizeNow * 1.5;
        ctx.beginPath();
        ctx.arc(px, pyNow, sizeNow / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Starburst on big sparks when young
        if (isBig && effectiveLifeFrac < 0.35) {
          ctx.save();
          ctx.globalAlpha = alpha * 0.7 * (1 - effectiveLifeFrac / 0.35);
          ctx.strokeStyle = CREAM;
          ctx.shadowColor = CREAM;
          ctx.shadowBlur = 10;

          const starSize = sizeNow * 2;
          const shortArm = starSize * 0.35;
          const angle = skewAngle + (s1 - 0.5) * 0.5;

          ctx.translate(px, pyNow);
          ctx.rotate(angle);

          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(0, -starSize); ctx.lineTo(0, starSize); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(-starSize, 0); ctx.lineTo(starSize, 0); ctx.stroke();

          ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(-shortArm, -shortArm); ctx.lineTo(shortArm, shortArm); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(shortArm, -shortArm); ctx.lineTo(-shortArm, shortArm); ctx.stroke();

          ctx.fillStyle = "white";
          ctx.shadowBlur = sizeNow * 2.5;
          ctx.shadowColor = "white";
          ctx.beginPath(); ctx.arc(0, 0, sizeNow * 0.3, 0, Math.PI * 2); ctx.fill();

          ctx.restore();
        }
      }
    }
  }

  // Logo is FIXED at screen center. The wipe panel acts as a mask —
  // the logo is only visible where the panel overlaps it. As the wipe
  // sweeps across, it reveals then conceals the logo like a traveling window.
  if (logoImg) {
    const logoH = 220;
    const logoW = logoImg.width * (logoH / logoImg.height);
    // Fixed screen-center position
    const logoX = (W - logoW) / 2;
    const logoY = (H - logoH) / 2;
    // Clip to the panel shape — logo only shows through the wipe
    ctx.save();
    ctx.beginPath();
    const x = panelLeft;
    ctx.moveTo(x + skewPx, 0);
    ctx.lineTo(x + panelW + skewPx, 0);
    ctx.lineTo(x + panelW, H);
    ctx.lineTo(x, H);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
    ctx.restore();
  }
}

// ── SPARKLE-WHOOSH SOUND ─────────────────────────────────────────────────────
// Plays the transition sound using Web Audio API. Same synthesis as the render
// script: filtered noise sweep (whoosh) + shimmery sparkle tones.
function playWhoosh() {
  const C = window.AudioContext || window.webkitAudioContext;
  if (!C) return;
  const ac = new C();
  if (ac.state === "suspended") { ac.close(); return; }

  const dur = TOTAL_MS / 1000 + 0.8; // extra tail for reverb
  const rate = ac.sampleRate;
  const len = Math.floor(rate * dur);

  // Lush reverb — longer tail, softer decay for that magical wash
  const reverbDur = 1.6;
  const reverbLen = Math.floor(rate * reverbDur);
  const reverbBuf = ac.createBuffer(2, reverbLen, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = reverbBuf.getChannelData(ch);
    for (let i = 0; i < reverbLen; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / reverbLen, 1.8);
    }
  }
  const reverb = ac.createConvolver();
  reverb.buffer = reverbBuf;

  // Lowpass filter on the output — rolls off harshness above 6kHz
  const softener = ac.createBiquadFilter();
  softener.type = "lowpass";
  softener.frequency.value = 6000;
  softener.Q.value = 0.5; // gentle slope

  // Dry/wet mixer — heavy reverb for dreamy wash
  const dryGain = ac.createGain();
  dryGain.gain.value = 0.35;
  const wetGain = ac.createGain();
  wetGain.gain.value = 0.7;

  dryGain.connect(softener);
  reverb.connect(wetGain);
  wetGain.connect(softener);
  softener.connect(ac.destination);

  // ── Layer 1: soft pad — a quiet sustained chord, no sweep ──
  const padNotes = [523.25, 659.25, 783.99]; // C5 E5 G5 — soft major chord
  padNotes.forEach(freq => {
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const g = ac.createGain();
    const animDur = TOTAL_MS / 1000;
    g.gain.setValueAtTime(0, ac.currentTime);
    g.gain.linearRampToValueAtTime(0.025, ac.currentTime + 0.15);
    g.gain.setValueAtTime(0.025, ac.currentTime + animDur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + animDur + 0.4);
    osc.connect(g);
    g.connect(reverb); // pad goes entirely through reverb
    osc.start(ac.currentTime);
    osc.stop(ac.currentTime + animDur + 0.5);
  });

  // ── Layer 2: crystal arpeggio — one long sparkle run ──
  // Cmaj9 voicing (C E G B D) across octaves 7-8 — jazzy, happy, bright.
  // Single ascending run spread across the full wipe duration.
  const sparkleNotes = [
    2093.0,  // C7
    2637.0,  // E7
    3136.0,  // G7
    3951.1,  // B7
    4698.6,  // D8
    5274.0,  // E8
    6271.9,  // G8
    7902.1,  // B8
    2349.3,  // D7 (color tone)
    4186.0,  // C8
    5587.7,  // F8 (lydian #4 — dreamy)
    6271.9,  // G8
    7458.6,  // A8
    8372.0,  // C9
  ];

  const arpStart = 0.06;
  const arpSpacing = 0.065; // ~15 notes per second — fast tinkle
  const noteDur = 0.45; // long tail with reverb

  sparkleNotes.forEach((freq, i) => {
    const noteStart = arpStart + i * arpSpacing;
    if (noteStart > dur - 0.2) return;

    // Primary voice — soft sine with gentle attack
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const g = ac.createGain();
    const vol = 0.035 - i * 0.001;
    g.gain.setValueAtTime(0, ac.currentTime + noteStart);
    g.gain.linearRampToValueAtTime(Math.max(0.01, vol), ac.currentTime + noteStart + 0.008);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + noteStart + noteDur);
    osc.connect(g);
    g.connect(dryGain);
    g.connect(reverb);
    osc.start(ac.currentTime + noteStart);
    osc.stop(ac.currentTime + noteStart + noteDur + 0.1);

    // Detuned second voice — slightly flat, very quiet, adds warmth/chorus
    const osc2 = ac.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = freq * 0.998; // ~3 cents flat
    const g2 = ac.createGain();
    g2.gain.setValueAtTime(0, ac.currentTime + noteStart);
    g2.gain.linearRampToValueAtTime(Math.max(0.005, vol * 0.4), ac.currentTime + noteStart + 0.012);
    g2.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + noteStart + noteDur * 0.8);
    osc2.connect(g2);
    g2.connect(reverb); // detuned voice goes mostly through reverb
    osc2.start(ac.currentTime + noteStart);
    osc2.stop(ac.currentTime + noteStart + noteDur);
  });

  // Cleanup after everything fades
  setTimeout(() => ac.close(), (dur + 1) * 1000);
}

// ── PREVIEW MODE ─────────────────────────────────────────────────────────────
function StingerPreview({ W, H, portrait }) {
  const canvasRef = useRef(null);
  const logoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { logoRef.current = img; setLoaded(true); };
    img.src = "/jop_logo_wide.png";
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let frame = 0;
    let raf;
    let soundPlayed = false;
    function tick() {
      drawFrame(ctx, frame, logoRef.current, W, H);
      // Play whoosh at the start of each loop
      if (frame === 1 && !soundPlayed) { playWhoosh(); soundPlayed = true; }
      frame++;
      // Auto-loop: pause 1.5s at the end then restart
      if (frame > TOTAL_FRAMES + 90) { frame = 0; soundPlayed = false; }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loaded, W, H]);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#222", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxWidth: "100%", maxHeight: "100%" }} />
      <div style={{ position: "absolute", bottom: "2rem", color: "white", fontSize: "0.9rem", opacity: 0.6 }}>
        Preview ({portrait ? "portrait 1080×1920" : "landscape 1920×1080"}) — add ?render to download
        the WebM file{portrait ? "" : ", or ?portrait for the vertical rig"}
      </div>
    </div>
  );
}

// ── RENDER MODE — generates downloadable WebM ───────────────────────────────
function StingerRender({ W, H, portrait }) {
  const [status, setStatus] = useState("Loading logo...");
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const logo = new Image();
    logo.onload = async () => {
      setStatus("Rendering frames...");

      // Use MediaRecorder on the canvas stream
      const stream = canvas.captureStream(0); // 0 = manual frame advance
      const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 8000000,
      });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      recorder.start();

      // Render each frame
      for (let f = 0; f <= TOTAL_FRAMES + 5; f++) {
        drawFrame(ctx, f, logo, W, H);
        // Request a frame from the stream
        const track = stream.getVideoTracks()[0];
        if (track.requestFrame) track.requestFrame();
        // Small delay to let the encoder process
        await new Promise(r => setTimeout(r, 1000 / FPS));
        setStatus(`Rendering frame ${f}/${TOTAL_FRAMES}...`);
      }

      // Clear final frame (transparent)
      ctx.clearRect(0, 0, W, H);
      const track = stream.getVideoTracks()[0];
      if (track.requestFrame) track.requestFrame();
      await new Promise(r => setTimeout(r, 100));

      recorder.stop();

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = portrait ? "jop-stinger-transition-portrait.webm" : "jop-stinger-transition.webm";
        a.click();
        setStatus("✅ Downloaded! Point OBS Stinger at the file. Transition point: 600ms.");
      };
    };
    logo.onerror = () => setStatus("Failed to load logo");
    logo.src = "/jop_logo_wide.png";
  }, []);

  return (
    <div style={{ width: "100vw", height: "100vh", background: "#0d1833", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2rem", fontFamily: "system-ui, sans-serif" }}>
      <canvas ref={canvasRef} width={W} height={H} style={{ maxWidth: "80%", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }} />
      <div style={{ color: "var(--jop-gold)", fontSize: "1.2rem", fontWeight: 700 }}>{status}</div>
      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", maxWidth: "500px", textAlign: "center", lineHeight: 1.6 }}>
        This page renders the stinger animation frame-by-frame and downloads it as a WebM video file.
        Use it as your OBS Stinger transition video with a transition point of 600ms.
      </div>
    </div>
  );
}

// ── ROUTER ───────────────────────────────────────────────────────────────────
export default function Stinger() {
  const params = new URLSearchParams(window.location.search);
  const isRender = params.has("render");
  // Explicit ?portrait wins; otherwise infer from the viewport shape so the
  // Preview Hub's portrait popout shows the right cut without extra params.
  const portrait = params.has("portrait")
    || (!params.has("landscape") && window.innerHeight > window.innerWidth);
  const { W, H } = DIMS[portrait ? "portrait" : "landscape"];
  return isRender
    ? <StingerRender W={W} H={H} portrait={portrait} />
    : <StingerPreview W={W} H={H} portrait={portrait} />;
}
